// path: src/pages/IPA/Negociacoes/pages/NegociacaoFornecedor/NegociacaoFornecedor.rowIdentity.ts
import { SuppliersExhibitionType } from '../../../../../store/useSuppliersExhibitionStore';

/** O campo da linha que nomeia a linha. Só existem dois, um por família de visão. */
export type RowIdentityKey = 'productsToBePricedId' | 'productId';

/** O campo que nomeia a linha nas visões por loja e por cluster, e nomeava em todas antes da rede. */
const DATAPOINT_ROW_KEY: RowIdentityKey = 'productsToBePricedId';

/** O agrupamento em que cada produto aparece numa linha só, consolidando todas as lojas do cliente. */
const NETWORK_EXHIBITION_TYPE: SuppliersExhibitionType = 'PRODUCT_SUPPLIERS_NETWORK';

/** Qual campo da linha é a identidade dela. */
export const resolveRowIdentityKey = (suppliersExhibitionType: SuppliersExhibitionType): RowIdentityKey =>
  suppliersExhibitionType === NETWORK_EXHIBITION_TYPE ? 'productId' : DATAPOINT_ROW_KEY;

/** O que a busca devolve quando nenhuma linha da página tem a identidade procurada. */
export const ROW_NOT_FOUND = -1;

/**
 * A identidade de uma linha como a grade a lê: ausência vira texto vazio. A busca abaixo normaliza
 * do mesmo jeito de propósito — comparar por uma regra e desenhar por outra é o começo de uma linha
 * selecionada não ser a linha atualizada.
 */
const normalizeRowIdentity = (value: unknown) => String(value ?? '');

/**
 * A identidade de uma linha, do jeito que a grade a lê: o campo que a visão elege, normalizado do
 * mesmo modo. Quem grava e quem desenha o carregamento no campo têm de nomear a linha por AQUI. Sem
 * visão informada a identidade é a do ponto de dado, que é o que a grade assume por omissão.
 */
export const resolveRowIdentity = (row: Partial<Record<RowIdentityKey, string | null>>, suppliersExhibitionType?: SuppliersExhibitionType): string =>
  normalizeRowIdentity(row[suppliersExhibitionType ? resolveRowIdentityKey(suppliersExhibitionType) : DATAPOINT_ROW_KEY]);

/**
 * A área do indicador de carregamento do detalhe de concorrência de uma linha. Quem busca o detalhe e
 * quem desenha o indicador têm de nomear a área por AQUI, e a partir da identidade da linha: montar o
 * nome com o código da loja produz "undefined" na linha de rede, que não carrega esse campo.
 */
export const competitorLoadingArea = (rowIdentity: string): string => `competitor-row-${rowIdentity}`;

/** O índice da linha cuja identidade é `rowIdentity`, dentro da página que está na tela. */
export const findRowIndexByIdentity = <TRow extends Partial<Record<RowIdentityKey, string | null>>>(
  rows: TRow[],
  rowIdentityKey: RowIdentityKey,
  rowIdentity: string | null | undefined,
): number => {
  const soughtIdentity = normalizeRowIdentity(rowIdentity);

  if (soughtIdentity === '') return ROW_NOT_FOUND;

  return rows.findIndex(row => normalizeRowIdentity(row[rowIdentityKey]) === soughtIdentity);
};

/**
 * O que a lista de linhas expandidas passa a ser quando o comprador pede para abrir ou fechar a
 * linha de identidade `rowIdentity`. Pedir a mesma linha de novo a fecha; pedir outra a acrescenta
 * sem tirar as que já estavam abertas.
 */
export const resolveNextExpandedRowKeys = (expandedRowKeys: string[], rowIdentity: string) => {
  let isOpen = false;
  const nextExpandedRowKeys: string[] = [];

  expandedRowKeys.forEach(key => {
    if (key === rowIdentity) {
      isOpen = true;
      return;
    }
    nextExpandedRowKeys.push(key);
  });

  if (!isOpen) {
    nextExpandedRowKeys.push(rowIdentity);
  }

  return { isOpen, nextExpandedRowKeys };
};
